/**
 * 历史牌局面板：牌局列表 → 本局轮次 → 单手详情。
 *
 * 服务端的列表/详情接口早就有（`HandRepo.listAccountHands` / `getReplay`），
 * 这是它们第一次被接上界面——和头像是同一种「能力在，没人搭入口」。
 *
 * 详情屏只给「board + 每个座位的结果 + 完整动作日志」，不做逐帧回放播放器：
 * 服务端的 `steps` 字段是给那种播放器用的引擎快照，这里的目标是「看得懂这手牌
 * 怎么打的」，一份分街分组的操作记录就够，不需要再造一套播放/拖动的交互。
 */
import { useCallback, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { toCardId } from '@poker/shared'
import type { HandSummary, PublicAction, Street } from '@poker/shared'

import { IconAlert } from '../lobby/lobbyIcons.js'
import { SheetShell } from '../auth/sheetShell.js'
import { Card } from '../table/Card.js'
import { STREET_TEXT_ZH, formatChips } from '../table/cardFormat.js'
import { actionLogLine, formatHandTime, formatNet, netTone } from './historyFormat.js'
import { HistoryError, getHandReplay, listMyHands, type HandReplay } from './historyApi.js'
import './history.css'

type Mode =
  | { readonly kind: 'browse'; readonly roomId: HandSummary['roomId'] | null }
  | { readonly kind: 'detail'; readonly handId: string; readonly roomId: HandSummary['roomId'] }

export interface HandGameGroup {
  readonly roomId: HandSummary['roomId']
  readonly latestEndedAt: number
  readonly hands: readonly HandSummary[]
}

/**
 * 一间房从创建到结算就是一局，`roomId` 是它稳定的边界。不能按日期或 handNo 猜：
 * 两局都可能有“第 1 手”，同一局也可能跨过午夜。Map 保留接口的最新优先顺序，
 * 因而“加载更多”补进旧手时会并回原来的牌局，而不是另造一个重复分组。
 */
export function groupHandsByGame(hands: readonly HandSummary[]): readonly HandGameGroup[] {
  const groups = new Map<HandSummary['roomId'], HandSummary[]>()
  for (const hand of hands) {
    const group = groups.get(hand.roomId)
    if (group === undefined) groups.set(hand.roomId, [hand])
    else group.push(hand)
  }
  return [...groups].map(([roomId, groupedHands]) => ({
    roomId,
    latestEndedAt: groupedHands[0]?.endedAt ?? 0,
    hands: groupedHands,
  }))
}

function gameNet(game: HandGameGroup): number {
  return game.hands.reduce((total, hand) => total + hand.heroNet, 0)
}

export interface HistoryPanelProps {
  onClose?(): void
  /** 注入点：测试用；默认打真接口 */
  readonly listHands?: (before?: string) => Promise<HandSummary[]>
  readonly loadReplay?: (handId: string) => Promise<HandReplay>
  readonly initialMode?: Mode
}

export function HistoryPanel({ onClose, listHands, loadReplay, initialMode }: HistoryPanelProps): ReactNode {
  const [mode, setMode] = useState<Mode>(initialMode ?? { kind: 'browse', roomId: null })

  return (
    <SheetShell title="历史牌局" {...(onClose === undefined ? {} : { onClose })}>
      {mode.kind === 'browse' ? (
        <HandListView
          {...(listHands === undefined ? {} : { listHands })}
          selectedRoomId={mode.roomId}
          onOpenGame={(roomId) => setMode({ kind: 'browse', roomId })}
          onBackToGames={() => setMode({ kind: 'browse', roomId: null })}
          onOpenHand={(handId) => {
            if (mode.roomId !== null) setMode({ kind: 'detail', handId, roomId: mode.roomId })
          }}
        />
      ) : (
        <HandDetailView
          handId={mode.handId}
          {...(loadReplay === undefined ? {} : { loadReplay })}
          onBack={() => setMode({ kind: 'browse', roomId: mode.roomId })}
          backLabel="返回本局轮次"
        />
      )}
    </SheetShell>
  )
}

// ============================================================================
// 列表屏
// ============================================================================

export function HandListView({
  listHands = (before) => listMyHands(before),
  selectedRoomId = null,
  onOpenGame = () => undefined,
  onBackToGames = () => undefined,
  onOpenHand,
  initialHands,
  initialLoading = true,
}: {
  readonly listHands?: (before?: string) => Promise<HandSummary[]>
  readonly selectedRoomId?: HandSummary['roomId'] | null
  onOpenGame?(roomId: HandSummary['roomId']): void
  onBackToGames?(): void
  onOpenHand(handId: string): void
  /** 测试用：SSR 静态渲染「已经拿到数据」那一刻，不必等 effect（renderToStaticMarkup 不跑 effect） */
  readonly initialHands?: readonly HandSummary[]
  readonly initialLoading?: boolean
}): ReactNode {
  const [hands, setHands] = useState<readonly HandSummary[]>(initialHands ?? [])
  const [loading, setLoading] = useState(initialLoading)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(true)
  const games = groupHandsByGame(hands)
  const selectedGame = selectedRoomId === null ? undefined : games.find((game) => game.roomId === selectedRoomId)

  const load = useCallback(
    (before: string | undefined, replace: boolean): void => {
      const setBusy = before === undefined ? setLoading : setLoadingMore
      setBusy(true)
      setError(null)
      void listHands(before).then(
        (page) => {
          setHands((prev) => (replace ? page : [...prev, ...page]))
          setHasMore(page.length > 0)
          setBusy(false)
        },
        (e: unknown) => {
          setError(e instanceof HistoryError || e instanceof Error ? e.message : '没能加载，再试一次')
          setBusy(false)
        },
      )
    },
    [listHands],
  )

  useEffect(() => {
    load(undefined, true)
    // 只在挂载时拉第一页；「加载更多」由按钮触发
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (loading) return <p className="acct-status" role="status">正在加载…</p>

  return (
    <div className="history-scroll">
      {error !== null && (
        <div className="lobby-error" role="alert">
          <IconAlert size={18} />
          <span>{error}</span>
        </div>
      )}
      {hands.length === 0 && error === null ? (
        <p className="acct-say">还没有打过牌，开一局试试。</p>
      ) : selectedRoomId === null ? (
        <div className="history-games">
          {games.map((game) => (
            <button key={game.roomId} type="button" className="history-game" onClick={() => onOpenGame(game.roomId)}>
              <span className="history-game__head">
                <span className="history-game__title">牌局 · {formatHandTime(game.latestEndedAt)}</span>
                <span className="history-game__arrow" aria-hidden="true">›</span>
              </span>
              <span className="history-game__meta">
                {/* 同一个房间玩法固定，看任意一手即可；只标非默认玩法 */}
                {game.hands.some((hand) => hand.deckVariant === 'shortDeck') && '短牌 · '}
                {game.hands.length} 手 · {Math.max(...game.hands.map((hand) => hand.playerCount))} 位玩家
              </span>
              <span className="history-game__result">
                <span>我的结果</span>
                <span className={`num history-row__net--${netTone(gameNet(game))}`}>{formatNet(gameNet(game))}</span>
              </span>
            </button>
          ))}
        </div>
      ) : selectedGame === undefined ? (
        <>
          <button type="button" className="lobby-btn lobby-btn--ghost history-back" onClick={onBackToGames}>
            ← 全部牌局
          </button>
          <p className="acct-say">这局暂时没有可查看的轮次。</p>
        </>
      ) : (
        <div className="history-game-view">
          <button type="button" className="lobby-btn lobby-btn--ghost history-back" onClick={onBackToGames}>
            ← 全部牌局
          </button>
          <div className="history-game-summary">
            <span>牌局 · {formatHandTime(selectedGame.latestEndedAt)}</span>
            <span>{selectedGame.hands.length} 手</span>
          </div>
          <ul className="history-list">
            {selectedGame.hands.map((h) => (
              <li key={h.handId}>
                <button type="button" className="history-row" onClick={() => onOpenHand(h.handId)}>
                  <span className="history-row__no">第 {h.handNo} 手</span>
                  <span className="history-row__time">{formatHandTime(h.endedAt)}</span>
                  <span className="history-row__hand">{h.heroHandLabel ?? (h.heroShowdown ? '' : '未摊牌')}</span>
                  <span className={`history-row__net num history-row__net--${netTone(h.heroNet)}`}>
                    {formatNet(h.heroNet)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {hasMore && hands.length > 0 && (
        <button
          type="button"
          className="lobby-btn lobby-btn--ghost"
          disabled={loadingMore}
          onClick={() => load(hands[hands.length - 1]?.handId, false)}
        >
          {loadingMore ? '正在加载…' : '加载更多'}
        </button>
      )}
    </div>
  )
}

// ============================================================================
// 详情屏
// ============================================================================

function groupByStreet(actions: readonly PublicAction[]): readonly (readonly [Street, readonly PublicAction[]])[] {
  const order: Street[] = ['preflop', 'flop', 'turn', 'river']
  return order
    .map((street) => [street, actions.filter((a) => a.street === street)] as const)
    .filter(([, list]) => list.length > 0)
}

export function HandDetailView({
  handId,
  loadReplay = (id) => getHandReplay(id),
  onBack,
  backLabel = '返回列表',
  initialReplay = null,
}: {
  readonly handId: string
  readonly loadReplay?: (handId: string) => Promise<HandReplay>
  onBack(): void
  readonly backLabel?: string
  /** 测试用：同上，跳过等 effect 那一步 */
  readonly initialReplay?: HandReplay | null
}): ReactNode {
  const [replay, setReplay] = useState<HandReplay | null>(initialReplay)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setReplay(null)
    setError(null)
    void loadReplay(handId).then(setReplay, (e: unknown) => {
      setError(e instanceof HistoryError || e instanceof Error ? e.message : '没能加载，再试一次')
    })
    // handId 变化才重新拉；loadReplay 注入点在测试里按引用稳定，生产环境是同一个函数
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handId])

  const nameOf = (seat: number): string =>
    replay?.seats.find((s) => s.seat === seat)?.nickname ?? `座位 ${String(seat + 1)}`

  return (
    <div className="history-scroll">
      <button type="button" className="lobby-btn lobby-btn--ghost history-back" onClick={onBack}>
        ← {backLabel}
      </button>

      {error !== null && (
        <div className="lobby-error" role="alert">
          <IconAlert size={18} />
          <span>{error}</span>
        </div>
      )}

      {replay === null && error === null && <p className="acct-status" role="status">正在加载…</p>}

      {replay !== null && (
        <>
          <div className="history-detail__head">
            <span>第 {replay.hand.handNo} 手</span>
            <span>{replay.hand.endedBy === 'showdown' ? '摊牌' : replay.hand.endedBy === 'fold' ? '弃牌获胜' : ''}</span>
          </div>

          {replay.hand.board.length > 0 && (
            <div className="compare__board" aria-label="公共牌">
              {replay.hand.board.map((code, i) => (
                <Card key={i} card={toCardId(code)} faceUp size="board" width={28} height={40} />
              ))}
            </div>
          )}

          <ul className="history-seats">
            {replay.seats.map((s) => (
              <li key={s.seat} className="history-seats__row">
                <span className="result__name">{s.nickname}</span>
                <span className="history-seats__stacks num">
                  {formatChips(s.startingStack)} → {formatChips(s.endingStack)}
                </span>
                <span className={`num history-row__net--${netTone(s.net)}`}>{formatNet(s.net)}</span>
              </li>
            ))}
          </ul>

          <p className="acct-say">操作记录</p>
          <div className="history-log">
            {groupByStreet(replay.actions).map(([street, list]) => (
              <div key={street} className="history-log__street">
                <p className="history-log__street-name">{STREET_TEXT_ZH[street]}</p>
                {list.map((a) => (
                  <p key={a.index} className="history-log__line">
                    {actionLogLine(a, nameOf(a.seat), replay.hand.bbSeat)}
                  </p>
                ))}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
